import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, type PushNotificationSchema } from '@capacitor/push-notifications';
import { BackendService } from './backend.service';
import { SessionService } from './session.service';
import { filter, switchMap, take } from 'rxjs/operators';
import { NotificationTokenRequest } from '../generated-api/models';

@Injectable({
  providedIn: 'root',
})
export class MobileService {
  private backend = inject(BackendService);
  private session = inject(SessionService);

  private nativeBridgeInitialized = false;

  initializeNativeBridge(): void {
    if (this.nativeBridgeInitialized || !Capacitor.isNativePlatform()) return;

    this.nativeBridgeInitialized = true;
    this.initializePushNotifications().catch(error => console.warn('Failed to initialize push notifications', error));
  }

  setNotificationToken(token: string): void {
    this.session.session$
      .pipe(
        filter(session => !!session),
        take(1),
        switchMap(() =>
          this.backend.request<void, NotificationTokenRequest>({
            apiRoute: 'user/notificationToken',
            type: 'update',
            data: { token },
          }),
        ),
      )
      .subscribe();
  }

  getAuthorizationHeader(): string {
    const jwt = this.session.storedJwt;
    return jwt ? `Bearer ${jwt}` : '';
  }

  private async initializePushNotifications(): Promise<void> {
    await PushNotifications.addListener('registration', token => this.setNotificationToken(token.value));
    await PushNotifications.addListener('registrationError', error =>
      console.warn('Push notification registration failed', error),
    );
    await PushNotifications.addListener('pushNotificationActionPerformed', event =>
      this.openPushNotificationTarget(event.notification),
    );

    const permission = await PushNotifications.checkPermissions();
    const resolvedPermission =
      permission.receive === 'prompt' ? await PushNotifications.requestPermissions() : permission;

    if (resolvedPermission.receive !== 'granted') return;

    await PushNotifications.register();
  }

  private openPushNotificationTarget(notification: PushNotificationSchema): void {
    const url = notification.data?.['url'];

    if (typeof url !== 'string' || url.length === 0) return;

    const targetUrl = this.parseNotificationTargetUrl(url);

    if (targetUrl == null) return;

    window.location.assign(targetUrl.href);
  }

  private parseNotificationTargetUrl(url: string): URL | undefined {
    let targetUrl: URL;

    try {
      targetUrl = new URL(url, window.location.origin);
    } catch {
      console.warn('Ignoring malformed push notification URL', { url });
      return undefined;
    }

    if (targetUrl.origin !== window.location.origin) {
      console.warn('Ignoring cross-origin push notification URL', { origin: targetUrl.origin });
      return undefined;
    }

    if (targetUrl.protocol !== 'https:' && targetUrl.protocol !== window.location.protocol) {
      console.warn('Ignoring push notification URL with unsupported protocol', { protocol: targetUrl.protocol });
      return undefined;
    }

    return targetUrl;
  }
}
